import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NavigationErrorHandlerService {
  private router = inject(Router);

  /**
   * Handles navigation errors with environment-aware logging.
   * 
   * Security Considerations:
   * - Development: Full error details including stack traces for debugging
   * - Production: Limited logging to prevent exposure of sensitive information
   * - All errors are still captured in Sentry with full context for monitoring
   */

  handleError(error: any): void {
    // Prepare error details (always needed for Sentry)
    const errorDetails = {
      message: error?.message,
      status: error?.status,
      url: error?.url,
      stack: error?.stack,
      timestamp: new Date().toISOString()
    };

    // Conditional logging based on environment
    if (!environment.production) {
      // Development: Log full error details including sensitive information
      console.error('Navigation error occurred:', error);
    } else {
      // Production: Log minimal, non-sensitive information only
      console.error('Navigation error occurred', {
        url: error?.url,
        status: error?.status,
        timestamp: errorDetails.timestamp
      });
    }
    
    // Add breadcrumb for navigation error context
    Sentry.addBreadcrumb({
      message: 'Navigation error occurred',
      category: 'navigation',
      data: {
        targetUrl: error?.url,
        currentUrl: window.location.href,
        errorType: error?.constructor?.name || 'Unknown'
      },
      level: 'error'
    });
    
    // Capture error in Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        errorType: 'navigation',
        statusCode: error?.status?.toString() || 'unknown',
        component: 'NavigationErrorHandler'
      },
      extra: {
        navigationUrl: error?.url,
        errorMessage: error?.message,
        timestamp: errorDetails.timestamp,
        userAgent: navigator.userAgent,
        currentRoute: window.location.pathname,
        referrer: document.referrer || 'direct'
      },
      level: 'error'
    });
    
    // Determine appropriate error page based on error type
    let errorRoute = '/error';
    let queryParams: any = {};
    
    if (error?.status === 403) {
      errorRoute = '/forbidden';
    } else if (error?.status === 401) {
      errorRoute = '/unauthorized';
    } else {
      // For other errors, pass error details to error page
      queryParams = {
        errorCode: error?.status || 500,
        message: encodeURIComponent(error?.message || 'An unexpected navigation error occurred')
      };
    }
    
    // Navigate to appropriate error page
    this.router.navigate([errorRoute], {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      replaceUrl: true // Replace current URL to prevent back navigation to broken route
    }).catch(navigationError => {
      // Fallback if navigation to error page also fails
      if (!environment.production) {
        // Development: Log full error details
        console.error('Failed to navigate to error page:', navigationError);
      } else {
        // Production: Log minimal information only
        console.error('Failed to navigate to error page', {
          attemptedRoute: errorRoute,
          timestamp: new Date().toISOString()
        });
      }
      
      // Capture secondary navigation error in Sentry
      Sentry.captureException(navigationError, {
        tags: {
          errorType: 'navigation-fallback',
          component: 'NavigationErrorHandler'
        },
        extra: {
          originalError: error?.message,
          originalUrl: error?.url,
          attemptedErrorRoute: errorRoute,
          fallbackAction: 'window.location.href redirect'
        },
        level: 'fatal'
      });
      
      window.location.href = '/error';
    });
  }
}